import {
  Card,
  Chip,
  Input,
  Select,
  Textarea,
  Toggle,
  Typography,
} from "@mg/dali/src";
import { ArrowLeft, Copy, Envelope, Spinner } from "@phosphor-icons/react";
import { Tabs, Button, Dialog, Flex } from "@radix-ui/themes";
// eslint-disable-next-line import/named
import { createRoute, redirect, useNavigate } from "@tanstack/react-router";
import { type ChangeEvent, useState, useEffect } from "react";

import { Markdown } from "../../../components/Markdown";

/** Strip <body>…</body> wrapper so rehype-raw can process the inner HTML */
function stripBody(html: string): string {
  return html.replace(/<\/?body[^>]*>/gi, "");
}
import { useUI } from "../../../contexts/ui";
import { requiresAuth, canManageUsers } from "../../../utils/auth";
import {
  useIntegrationSettings,
  useUpdateIntegrationSettings,
  useIntegrationHealth,
  useTriggerIntegrationTest,
  useIntegrationEventStatus,
  useRecentIntegrationTasks,
} from "../../../utils/queries/enterprises";
import { authLayoutRoute } from "../../auth-layout/route";
import { ticketsRoute } from "../../tickets/route";
import { settingsRoute } from "../route";

export const integrationSettingsRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "integration-settings",
  beforeLoad() {
    if (!canManageUsers()) {
      throw redirect({ to: ticketsRoute.to } as any);
    }
    requiresAuth();
  },
  component: IntegrationSettings,
});

interface TriggerConfig {
  eventType?: string;
  statusName?: string;
  skipPunttUserCheck?: boolean;
  assignSelfOnTrigger?: boolean;
}

interface CommentBehavior {
  postProgressComments?: boolean;
  postCompletionComments?: boolean;
  skipAllComments?: boolean;
  skipLinkUpdates?: boolean;
}

interface DesignReviewRule {
  descriptionContains: string;
  tagUsersOnCompletion?: boolean;
  skipStatusUpdate?: boolean;
}

interface EmailReplySettings {
  replyWithLink?: boolean;
  replyWithComments?: boolean;
  replyPrompt?: string;
}

interface IntegrationConfig {
  wrike?: {
    enabled?: boolean;
    user?: string;
    responseActions?: any;
    triggerConfig?: TriggerConfig;
    commentBehavior?: CommentBehavior;
    designReviewRules?: DesignReviewRule[];
  };
  asana?: {
    enabled?: boolean;
    commentBehavior?: CommentBehavior;
    designReviewRules?: DesignReviewRule[];
  };
  email?: {
    enabled?: boolean;
    replySettings?: EmailReplySettings;
    allowedDomains?: string[];
    allowedSenderPatterns?: string[];
  };
}

type IntegrationTab = "wrike" | "asana" | "email";

const DEFAULT_REPLY_PROMPT =
  "Summarize the AI review findings concisely. Include the number of issues found, their severity, and key recommendations.";

const EVENT_TYPE_OPTIONS = [
  { label: "Task Status Changed", value: "TaskStatusChanged" },
  { label: "Task Created", value: "TaskCreated" },
  { label: "Task Updated", value: "TaskUpdated" },
];

function HealthBadge({ status }: { status: any; loading?: boolean }) {
  if (!status) return null;

  if (status.connected && status.status === "ACTIVE") {
    return (
      <span className="ml-2 inline-flex items-center gap-1">
        <span className="bg-green-500 size-2 rounded-full" />
        <Typography size="xs" className="text-green-600">
          Connected
        </Typography>
      </span>
    );
  }

  if (!status.connected) {
    return (
      <span className="ml-2 inline-flex items-center gap-1">
        <span className="bg-red-500 size-2 rounded-full" />
        <Typography size="xs" className="text-red-500">
          Not connected
        </Typography>
      </span>
    );
  }

  return (
    <span className="ml-2 inline-flex items-center gap-1">
      <span className="bg-yellow-500 size-2 rounded-full" />
      <Typography size="xs" className="text-yellow-600">
        {status.status}
      </Typography>
    </span>
  );
}

function EmailHealthBadge({ status }: { status: any }) {
  if (!status) return null;
  const ok = status.enabled && status.postmarkConfigured;
  return (
    <span className="ml-2 inline-flex items-center gap-1">
      <span
        className={`size-2 rounded-full ${ok ? "bg-green-500" : "bg-red-500"}`}
      />
      <Typography size="xs" className={ok ? "text-green-600" : "text-red-500"}>
        {ok
          ? "Active"
          : status.enabled
            ? "Postmark not configured"
            : "Disabled"}
      </Typography>
    </span>
  );
}

function connectionChipVariant(health: any) {
  if (!health || !health.connected) return "carbon-light" as const;
  switch (health.status) {
    case "ACTIVE":
      return "malachite" as const;
    case "FAILED":
    case "EXPIRED":
      return "vermillion" as const;
    case "INITIATED":
      return "cadmium" as const;
    default:
      return "carbon-light" as const;
  }
}

function connectionLabel(health: any) {
  if (!health) return "Not configured";
  if (!health.connected) return "Not connected";
  return health.status;
}

function ConnectionCard({ name, conn }: { name: string; conn: any }) {
  return (
    <div className="grid gap-2 rounded border p-3">
      <div className="flex items-center justify-between">
        <Typography size="sm" weight="bold">
          {name}
        </Typography>
        <div className="flex items-center gap-2">
          <Chip
            label={connectionLabel(conn)}
            variant={connectionChipVariant(conn)}
            size="sm"
          />
          {conn?.isDisabled && (
            <Chip label="Disabled" variant="vermillion" size="sm" />
          )}
        </div>
      </div>
      {conn?.connected && (
        <div className="grid gap-1">
          {conn.accountId && (
            <div className="flex items-center justify-between">
              <Typography size="xs" className="text-gray-500">
                Account
              </Typography>
              <button
                className="text-gray-600 hover:text-gray-800 flex items-center gap-1 text-xs"
                onClick={() => navigator.clipboard.writeText(conn.accountId)}
              >
                <span className="font-mono max-w-[140px] truncate">
                  {conn.accountId}
                </span>
                <Copy size={12} />
              </button>
            </div>
          )}
          {conn.updatedAt && (
            <Typography size="xs" className="text-gray-400">
              Updated {new Date(conn.updatedAt).toLocaleDateString()}
            </Typography>
          )}
        </div>
      )}
    </div>
  );
}

function ComposioStatusCard({
  wrike,
  asana,
  email,
}: {
  wrike: any;
  asana: any;
  email: any;
}) {
  return (
    <Card className="mb-4">
      <div className="grid gap-3 p-4">
        <Typography size="sm" weight="bold">
          Connection Status
        </Typography>
        <div className="grid grid-cols-3 gap-4">
          <ConnectionCard name="Wrike" conn={wrike} />
          <ConnectionCard name="Asana" conn={asana} />
          <div className="grid gap-2 rounded border p-3">
            <div className="flex items-center justify-between">
              <Typography size="sm" weight="bold">
                Email
              </Typography>
              <Chip
                label={
                  email?.enabled
                    ? email?.postmarkConfigured
                      ? "Active"
                      : "No Postmark"
                    : "Disabled"
                }
                variant={
                  email?.enabled && email?.postmarkConfigured
                    ? "malachite"
                    : "carbon-light"
                }
                size="sm"
              />
            </div>
            {email?.aiEmailAddress && (
              <div className="flex items-center justify-between">
                <Typography size="xs" className="text-gray-500">
                  AI Address
                </Typography>
                <button
                  className="text-gray-600 hover:text-gray-800 flex items-center gap-1 text-xs"
                  onClick={() =>
                    navigator.clipboard.writeText(email.aiEmailAddress)
                  }
                >
                  <span className="font-mono max-w-[140px] truncate">
                    {email.aiEmailAddress}
                  </span>
                  <Copy size={12} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

function RuleBehaviorPreview({ rule }: { rule: DesignReviewRule }) {
  const parts: string[] = [];
  if (rule.tagUsersOnCompletion) {
    parts.push("Posts comment with @-mentions + review link");
  } else {
    parts.push("Posts plain review link, sets status to Completed");
  }
  if (rule.skipStatusUpdate) {
    parts.push("does NOT update status");
  }

  return (
    <div className="bg-gray-50 rounded p-2">
      <Typography size="xs" className="text-gray-600">
        On match: {parts.join(", ")}
      </Typography>
    </div>
  );
}

function ReplyList({
  replies,
  replyCardClass,
}: {
  replies?: { type: string; content: string }[];
  replyCardClass: (type: string) => string;
}) {
  if (!replies || replies.length === 0) return null;
  return (
    <div className="grid gap-2">
      {replies.map((reply, i) => (
        <div key={i} className={replyCardClass(reply.type)}>
          <Markdown className="text-xs">{stripBody(reply.content)}</Markdown>
        </div>
      ))}
    </div>
  );
}

function TestIntegrationDialog({
  platform,
  open,
  onOpenChange,
}: {
  platform: IntegrationTab;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [taskId, setTaskId] = useState("");
  const [compositeTaskId, setCompositeTaskId] = useState<string | null>(null);
  const testMutation = useTriggerIntegrationTest();
  const { data: recentTasks } = useRecentIntegrationTasks(platform);
  const { data: eventStatus } = useIntegrationEventStatus(compositeTaskId, {
    enabled: !!compositeTaskId,
  });

  // Reset state when platform changes (tab switch)
  useEffect(() => {
    setTaskId("");
    setCompositeTaskId(null);
    testMutation.reset();
  }, [platform]);

  const status = eventStatus as
    | {
        state?: string;
        run_count?: number;
        task_name?: string;
        replies?: { type: string; content: string }[];
        createdAt?: string;
        updatedAt?: string;
      }
    | undefined;

  const handleRunTest = async () => {
    try {
      const result: { compositeTaskId?: string } =
        (await testMutation.mutateAsync({ platform, taskId })) as any;
      if (result?.compositeTaskId) {
        setCompositeTaskId(result.compositeTaskId);
      }
    } catch {
      // error state handled by mutation
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setTaskId("");
    setCompositeTaskId(null);
    testMutation.reset();
  };

  const isDone = status?.state === "completed" || status?.state === "failed";
  const isRunning = compositeTaskId && !isDone;
  const placeholder =
    platform === "wrike" ? "e.g. IEAG2JQWI" : "e.g. 1234567890";

  const replyCardClass = (type: string) => {
    switch (type) {
      case "error_message":
        return "rounded border border-red-200 bg-red-50 p-3";
      case "completion":
        return "rounded border border-green-200 bg-green-50 p-3";
      case "retry_prompt":
        return "rounded border border-yellow-200 bg-yellow-50 p-3";
      default:
        return "rounded border border-gray-200 bg-gray-50 p-3";
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content maxWidth="500px">
        <Dialog.Title>
          Test {platform === "wrike" ? "Wrike" : "Asana"} Integration
        </Dialog.Title>
        <Dialog.Description size="2" color="gray">
          Triggers a real integration workflow. The review will run and post a
          comment to the task (3-15 min).
        </Dialog.Description>

        <Flex direction="column" gap="4" mt="4">
          {/* Phase 1: Input */}
          {!compositeTaskId && !testMutation.isPending && (
            <>
              <Input
                label="Task ID"
                placeholder={placeholder}
                value={taskId}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setTaskId(e.target.value)
                }
              />
              {recentTasks && (recentTasks as any[]).length > 0 && (
                <div className="grid gap-1">
                  <Typography size="xs" className="text-gray-500">
                    Recent tasks:
                  </Typography>
                  <div className="flex flex-wrap gap-1">
                    {(recentTasks as any[]).map((t: any) => (
                      <button
                        key={t.taskId}
                        type="button"
                        className="hover:bg-gray-100 rounded border px-2 py-1 text-xs"
                        onClick={() => setTaskId(t.taskId)}
                      >
                        {t.taskName || t.taskId}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {testMutation.isError && (
                <Typography size="xs" className="text-red-500">
                  {testMutation.error instanceof Error
                    ? testMutation.error.message
                    : "An unknown error occurred"}
                </Typography>
              )}
            </>
          )}

          {/* Phase 2: Running */}
          {(testMutation.isPending || isRunning) && (
            <div className="grid gap-3">
              <div className="flex items-center gap-2">
                <Spinner size={16} className="animate-spin" />
                <Typography size="sm">
                  {testMutation.isPending
                    ? "Starting test..."
                    : `Status: ${status?.state || "pending"}`}
                </Typography>
              </div>
              {compositeTaskId && (
                <Typography size="xs" className="font-mono text-gray-400">
                  {compositeTaskId}
                </Typography>
              )}
              <Typography size="xs" className="text-gray-500">
                You can close this dialog, the test continues in background.
              </Typography>
              <ReplyList
                replies={status?.replies}
                replyCardClass={replyCardClass}
              />
            </div>
          )}

          {/* Phase 3: Done */}
          {isDone && (
            <div className="grid gap-3">
              <div className="flex items-center gap-2">
                <Chip
                  label={status?.state === "completed" ? "Completed" : "Failed"}
                  variant={
                    status?.state === "completed" ? "malachite" : "vermillion"
                  }
                  size="sm"
                />
                {status?.task_name && (
                  <Typography size="sm" className="text-gray-600">
                    {status.task_name}
                  </Typography>
                )}
              </div>
              <ReplyList
                replies={status?.replies}
                replyCardClass={replyCardClass}
              />
            </div>
          )}
        </Flex>

        <Flex gap="3" mt="4" justify="end">
          {!compositeTaskId && !testMutation.isPending ? (
            <>
              <Dialog.Close>
                <Button variant="soft" color="gray">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button onClick={handleRunTest} disabled={!taskId.trim()}>
                Run Test
              </Button>
            </>
          ) : (
            <Button variant="soft" color="gray" onClick={handleClose}>
              Close
            </Button>
          )}
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}

function IntegrationSettings() {
  const { data, isLoading } = useIntegrationSettings();
  const updateMutation = useUpdateIntegrationSettings();
  const { data: health } = useIntegrationHealth();
  const { notify } = useUI();
  const navigate = useNavigate();

  const [config, setConfig] = useState<IntegrationConfig>({});
  const [activeTab, setActiveTab] = useState<IntegrationTab>("wrike");
  const [isDirty, setIsDirty] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [testDialogOpen, setTestDialogOpen] = useState(false);

  useEffect(() => {
    if (data) {
      setConfig(data as IntegrationConfig);
      setIsDirty(false);
    }
  }, [data]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Trigger config: eventType and statusName must be set together
    if (activeTab === "wrike") {
      const trigger = config.wrike?.triggerConfig;
      if (trigger?.eventType && !trigger?.statusName) {
        newErrors["statusName"] =
          "Status Name is required when Event Type is set";
      }
      if (trigger?.statusName && !trigger?.eventType) {
        newErrors["eventType"] =
          "Event Type is required when Status Name is set";
      }
    }

    // Design review rules: descriptionContains must be non-empty (wrike/asana only)
    if (activeTab !== "email") {
      const rules = config[activeTab]?.designReviewRules || [];
      rules.forEach((rule, index) => {
        if (!rule.descriptionContains.trim()) {
          newErrors[`rule-${index}`] = "Description match text is required";
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      await updateMutation.mutateAsync(config);
      setIsDirty(false);
      notify({ title: "Integration settings saved", variant: "success" });
    } catch (error) {
      notify({
        title: "Error saving integration settings",
        message:
          error instanceof Error ? error.message : "An unknown error occurred",
        variant: "error",
      });
    }
  };

  const clearFieldError = (field: string) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const updateEnabled = (platform: IntegrationTab, value: boolean) => {
    setIsDirty(true);
    setConfig((prev) => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        enabled: value,
      },
    }));
  };

  const updateTriggerConfig = (field: keyof TriggerConfig, value: any) => {
    setIsDirty(true);
    clearFieldError(field);
    setConfig((prev) => ({
      ...prev,
      wrike: {
        ...prev.wrike,
        triggerConfig: {
          ...prev.wrike?.triggerConfig,
          [field]: value,
        },
      },
    }));
  };

  const updateCommentBehavior = (
    platform: "wrike" | "asana",
    field: keyof CommentBehavior,
    value: boolean,
  ) => {
    setIsDirty(true);
    setConfig((prev) => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        commentBehavior: {
          ...prev[platform]?.commentBehavior,
          [field]: value,
        },
      },
    }));
  };

  const updateEmailReplySettings = (
    field: keyof EmailReplySettings,
    value: any,
  ) => {
    setIsDirty(true);
    setConfig((prev) => ({
      ...prev,
      email: {
        ...prev.email,
        replySettings: {
          ...prev.email?.replySettings,
          [field]: value,
        },
      },
    }));
  };

  const updateEmailListField = (
    field: "allowedDomains" | "allowedSenderPatterns",
    value: string,
  ) => {
    setIsDirty(true);
    const items = value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    setConfig((prev) => ({
      ...prev,
      email: {
        ...prev.email,
        [field]: items.length > 0 ? items : undefined,
      },
    }));
  };

  const updateDesignReviewRule = (
    platform: "wrike" | "asana",
    index: number,
    field: keyof DesignReviewRule,
    value: any,
  ) => {
    setIsDirty(true);
    if (field === "descriptionContains") {
      clearFieldError(`rule-${index}`);
    }
    setConfig((prev) => {
      const rules = [...(prev[platform]?.designReviewRules || [])];
      rules[index] = { ...rules[index], [field]: value };
      return {
        ...prev,
        [platform]: {
          ...prev[platform],
          designReviewRules: rules,
        },
      };
    });
  };

  const addDesignReviewRule = (platform: "wrike" | "asana") => {
    setIsDirty(true);
    setConfig((prev) => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        designReviewRules: [
          ...(prev[platform]?.designReviewRules || []),
          {
            descriptionContains: "",
            tagUsersOnCompletion: false,
            skipStatusUpdate: false,
          },
        ],
      },
    }));
  };

  const removeDesignReviewRule = (
    platform: "wrike" | "asana",
    index: number,
  ) => {
    if (!window.confirm("Remove this rule?")) return;
    setIsDirty(true);
    clearFieldError(`rule-${index}`);
    setConfig((prev) => {
      const rules = [...(prev[platform]?.designReviewRules || [])];
      rules.splice(index, 1);
      return {
        ...prev,
        [platform]: {
          ...prev[platform],
          designReviewRules: rules,
        },
      };
    });
  };

  if (isLoading) {
    return (
      <article className="flex flex-col gap-4 overflow-auto px-10 py-6">
        <Typography as="h1" size="2xl" weight="bold">
          Integration Settings
        </Typography>
        <div className="flex items-center gap-2">
          <Spinner size={24} className="animate-spin" />
          <Typography>Loading settings...</Typography>
        </div>
      </article>
    );
  }

  const currentPlatform = config[activeTab];
  const isEnabled =
    activeTab === "email"
      ? currentPlatform?.enabled === true
      : currentPlatform?.enabled !== false;
  const commentBehavior =
    (activeTab !== "email" && (currentPlatform as any)?.commentBehavior) || {};
  const skipAll = commentBehavior.skipAllComments || false;
  const designReviewRules: DesignReviewRule[] =
    activeTab !== "email"
      ? (config[activeTab] as any)?.designReviewRules || []
      : [];

  const selectedEventType = EVENT_TYPE_OPTIONS.find(
    (o) => o.value === config.wrike?.triggerConfig?.eventType,
  );

  const healthData = health as
    | { wrike?: any; asana?: any; email?: any }
    | undefined;

  return (
    <article
      data-testid="integration-settings-page"
      className="flex flex-col gap-4 overflow-auto px-10 py-6"
    >
      <button
        data-testid="integration-settings-back-btn"
        onClick={() => navigate({ to: settingsRoute.to })}
        className="text-gray-500 hover:text-gray-700 flex items-center gap-2"
      >
        <ArrowLeft size={16} />
        <Typography size="sm">Back to Settings</Typography>
      </button>

      <header className="flex items-center justify-between">
        <Typography as="h1" size="2xl" weight="bold">
          Integration Settings
        </Typography>
        <Button
          data-testid="integration-settings-save-btn"
          onClick={handleSave}
          disabled={updateMutation.isPending || !isDirty}
        >
          {updateMutation.isPending
            ? "Saving..."
            : isDirty
              ? "Save Changes"
              : "Save"}
        </Button>
      </header>

      {/* Connection Status — all platforms */}
      <ComposioStatusCard
        wrike={healthData?.wrike}
        asana={healthData?.asana}
        email={healthData?.email}
      />

      {/* Platform Tabs */}
      <Tabs.Root
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as IntegrationTab)}
      >
        <Tabs.List>
          <Tabs.Trigger
            data-testid="integration-settings-tab-wrike"
            value="wrike"
          >
            Wrike
            <HealthBadge status={healthData?.wrike} />
          </Tabs.Trigger>
          <Tabs.Trigger
            data-testid="integration-settings-tab-asana"
            value="asana"
          >
            Asana
            <HealthBadge status={healthData?.asana} />
          </Tabs.Trigger>
          <Tabs.Trigger value="email">
            <Envelope size={16} className="mr-1" />
            Email
            <EmailHealthBadge status={healthData?.email} />
          </Tabs.Trigger>
        </Tabs.List>

        <div className="mt-2">
          {/* Enable/Disable Toggle + Test Button */}
          <Card className="mb-4">
            <div className="flex items-center justify-between p-4">
              <div>
                <Typography size="sm" weight="bold">
                  Enable{" "}
                  {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}{" "}
                  Integration
                </Typography>
                <Typography size="xs" className="text-gray-500">
                  {activeTab === "email"
                    ? isEnabled
                      ? "Email integration is active. Users can send emails to trigger AI reviews."
                      : "Email integration is disabled. Inbound emails will be silently dropped."
                    : isEnabled
                      ? "This integration is active and will process incoming events."
                      : "This integration is disabled. Incoming events will be ignored."}
                </Typography>
              </div>
              <div className="flex items-center gap-3">
                {isEnabled && activeTab !== "email" && (
                  <Button
                    data-testid="integration-settings-test-btn"
                    variant="outline"
                    onClick={() => setTestDialogOpen(true)}
                  >
                    Test Integration
                  </Button>
                )}
                <Toggle
                  data-testid="integration-settings-enable-toggle"
                  label={<span className="sr-only">Enable integration</span>}
                  value="enabled"
                  checked={isEnabled}
                  onChange={(e) => updateEnabled(activeTab, e.target.checked)}
                />
              </div>
            </div>
          </Card>

          {/* Email-specific: AI Email Address display */}
          {activeTab === "email" && healthData?.email?.aiEmailAddress && (
            <Card className="mb-4">
              <div className="grid gap-2 p-4">
                <Typography size="sm" weight="bold">
                  AI Email Address
                </Typography>
                <Typography size="xs" className="text-gray-500">
                  Users send emails to this address to trigger AI reviews.
                </Typography>
                <div className="bg-gray-50 flex items-center justify-between rounded border p-3">
                  <Typography size="sm" className="font-mono">
                    {healthData.email.aiEmailAddress}
                  </Typography>
                  <button
                    className="text-gray-600 hover:text-gray-800 flex items-center gap-1 text-sm"
                    onClick={() => {
                      navigator.clipboard.writeText(
                        healthData.email.aiEmailAddress,
                      );
                      notify({
                        title: "Email address copied",
                        variant: "success",
                      });
                    }}
                  >
                    <Copy size={14} />
                    Copy
                  </button>
                </div>
              </div>
            </Card>
          )}
          {activeTab === "email" &&
            !healthData?.email?.aiEmailAddress &&
            config.email?.enabled && (
              <Card className="mb-4">
                <div className="p-4">
                  <Typography size="xs" className="text-gray-400">
                    Save settings to generate your inbound address.
                  </Typography>
                </div>
              </Card>
            )}

          {/* Attachment Limits — Email only */}
          {activeTab === "email" && (
            <Card className="mb-4">
              <div className="grid gap-2 p-4">
                <Typography size="sm" weight="bold">
                  Attachment Limits
                </Typography>
                <Typography size="xs" className="text-gray-500">
                  Maximum file size per attachment: 25 MB. For larger files,
                  include a download link in the email body instead.
                </Typography>
              </div>
            </Card>
          )}

          <div className={!isEnabled ? "pointer-events-none opacity-50" : ""}>
            {/* Email Reply Settings — Email only */}
            {activeTab === "email" && (
              <Card className="mb-4">
                <div className="grid gap-4 p-4">
                  <Typography as="h2" size="lg" weight="bold">
                    Reply Settings
                  </Typography>
                  <Typography size="sm" className="text-gray-500">
                    Configure what gets included in the email reply after a
                    review completes.
                  </Typography>

                  <div className="grid gap-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <Typography size="sm" weight="bold">
                          Reply with Review Link
                        </Typography>
                        <Typography size="xs" className="text-gray-500">
                          Include a link to the full review in the reply email.
                        </Typography>
                      </div>
                      <Toggle
                        label={
                          <span className="sr-only">
                            Reply with Review Link
                          </span>
                        }
                        value="replyWithLink"
                        checked={
                          config.email?.replySettings?.replyWithLink !== false
                        }
                        onChange={(e) =>
                          updateEmailReplySettings(
                            "replyWithLink",
                            e.target.checked,
                          )
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Typography size="sm" weight="bold">
                          Include AI Comments in Reply
                        </Typography>
                        <Typography size="xs" className="text-gray-500">
                          Add a summary of AI review comments in the reply email
                          body.
                        </Typography>
                      </div>
                      <Toggle
                        label={
                          <span className="sr-only">
                            Include AI Comments in Reply
                          </span>
                        }
                        value="replyWithComments"
                        checked={
                          config.email?.replySettings?.replyWithComments ||
                          false
                        }
                        onChange={(e) =>
                          updateEmailReplySettings(
                            "replyWithComments",
                            e.target.checked,
                          )
                        }
                      />
                    </div>

                    {config.email?.replySettings?.replyWithComments && (
                      <div className="grid gap-2">
                        <Typography size="sm" weight="bold">
                          Reply Prompt
                        </Typography>
                        <Typography size="xs" className="text-gray-500">
                          Instructions for what the AI should include in the
                          email reply body.
                        </Typography>
                        <Textarea
                          placeholder={DEFAULT_REPLY_PROMPT}
                          value={
                            config.email?.replySettings?.replyPrompt ||
                            DEFAULT_REPLY_PROMPT
                          }
                          onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                            updateEmailReplySettings(
                              "replyPrompt",
                              e.target.value,
                            )
                          }
                          rows={3}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            )}

            {/* Sender Restrictions — Email only */}
            {activeTab === "email" && (
              <Card className="mb-4">
                <div className="grid gap-3 p-4">
                  <Typography as="h2" size="lg" weight="bold">
                    Sender Restrictions
                  </Typography>
                  <Typography size="sm" className="text-gray-500">
                    Restrict which senders can trigger reviews. Leave both empty
                    to allow all enterprise users.
                  </Typography>
                  <Input
                    label="Allowed Domains (comma-separated)"
                    placeholder="e.g., acme.com, partner.org"
                    value={config.email?.allowedDomains?.join(", ") || ""}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      updateEmailListField("allowedDomains", e.target.value)
                    }
                  />
                  <Input
                    label="Allowed Sender Patterns (comma-separated regexes)"
                    placeholder="e.g., ^john\..*@acme\.com$, .*@(sales|marketing)\.acme\.com$"
                    value={
                      config.email?.allowedSenderPatterns?.join(", ") || ""
                    }
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      updateEmailListField(
                        "allowedSenderPatterns",
                        e.target.value,
                      )
                    }
                  />
                  <Typography size="xs" className="text-gray-400">
                    A sender is allowed if their domain matches OR their full
                    email matches any pattern.
                  </Typography>
                </div>
              </Card>
            )}

            {/* Trigger Configuration - Wrike only */}
            {activeTab === "wrike" && (
              <Card className="mb-4">
                <div className="grid gap-4 p-4">
                  <Typography as="h2" size="lg" weight="bold">
                    Trigger Configuration
                  </Typography>
                  <Typography size="sm" className="text-gray-500">
                    Configure when a review gets triggered for this integration.
                  </Typography>

                  <div className="grid gap-3">
                    <Select<false>
                      data-testid="integration-settings-event-type-select"
                      label="Event Type"
                      placeholder="Select an event type"
                      options={EVENT_TYPE_OPTIONS}
                      value={selectedEventType}
                      onChange={(option) =>
                        updateTriggerConfig("eventType", option?.value || "")
                      }
                    />
                    {errors["eventType"] && (
                      <Typography size="xs" className="text-red-500">
                        {errors["eventType"]}
                      </Typography>
                    )}
                    <Input
                      data-testid="integration-settings-status-input"
                      label="Status Name"
                      placeholder="e.g., Internal QC"
                      value={config.wrike?.triggerConfig?.statusName || ""}
                      invalid={!!errors["statusName"]}
                      helpText={errors["statusName"] || ""}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        updateTriggerConfig("statusName", e.target.value)
                      }
                    />

                    <div className="flex items-center justify-between">
                      <div>
                        <Typography size="sm" weight="bold">
                          Skip Puntt User Check
                        </Typography>
                        <Typography size="xs" className="text-gray-500">
                          Trigger for all tasks, not just those assigned to the
                          Puntt user.
                        </Typography>
                      </div>
                      <Toggle
                        data-testid="integration-settings-skip-user-check-toggle"
                        label={
                          <span className="sr-only">Skip Puntt User Check</span>
                        }
                        value="skipPunttUserCheck"
                        checked={
                          config.wrike?.triggerConfig?.skipPunttUserCheck ||
                          false
                        }
                        onChange={(e) =>
                          updateTriggerConfig(
                            "skipPunttUserCheck",
                            e.target.checked,
                          )
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Typography size="sm" weight="bold">
                          Assign Self on Trigger
                        </Typography>
                        <Typography size="xs" className="text-gray-500">
                          Assign the Puntt user to the task when triggered.
                        </Typography>
                      </div>
                      <Toggle
                        data-testid="integration-settings-assign-self-toggle"
                        label={
                          <span className="sr-only">
                            Assign Self on Trigger
                          </span>
                        }
                        value="assignSelfOnTrigger"
                        checked={
                          config.wrike?.triggerConfig?.assignSelfOnTrigger ||
                          false
                        }
                        onChange={(e) =>
                          updateTriggerConfig(
                            "assignSelfOnTrigger",
                            e.target.checked,
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Comment/Response Behavior — Wrike/Asana only */}
            {activeTab !== "email" && (
              <Card className="mb-4">
                <div className="grid gap-4 p-4">
                  <Typography as="h2" size="lg" weight="bold">
                    Response Behavior
                  </Typography>
                  <Typography size="sm" className="text-gray-500">
                    Control how the integration responds with comments and
                    links.
                  </Typography>

                  <div className="grid gap-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <Typography size="sm" weight="bold">
                          Skip All Comments
                        </Typography>
                        <Typography size="xs" className="text-gray-500">
                          Disable all comment posting for this integration.
                        </Typography>
                      </div>
                      <Toggle
                        data-testid="integration-settings-skip-comments-toggle"
                        label={
                          <span className="sr-only">Skip All Comments</span>
                        }
                        value="skipAllComments"
                        checked={skipAll}
                        onChange={(e) =>
                          updateCommentBehavior(
                            activeTab,
                            "skipAllComments",
                            e.target.checked,
                          )
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Typography
                          size="sm"
                          weight="bold"
                          className={skipAll ? "text-gray-400" : ""}
                        >
                          Post Progress Comments
                        </Typography>
                        <Typography size="xs" className="text-gray-500">
                          {skipAll
                            ? "Disabled because 'Skip All Comments' is enabled."
                            : "Post comments when review starts processing."}
                        </Typography>
                      </div>
                      <Toggle
                        data-testid="integration-settings-progress-comments-toggle"
                        label={
                          <span className="sr-only">
                            Post Progress Comments
                          </span>
                        }
                        value="postProgressComments"
                        checked={
                          skipAll
                            ? false
                            : commentBehavior.postProgressComments !== false
                        }
                        disabled={skipAll}
                        onChange={(e) =>
                          updateCommentBehavior(
                            activeTab,
                            "postProgressComments",
                            e.target.checked,
                          )
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Typography
                          size="sm"
                          weight="bold"
                          className={skipAll ? "text-gray-400" : ""}
                        >
                          Post Completion Comments
                        </Typography>
                        <Typography size="xs" className="text-gray-500">
                          {skipAll
                            ? "Disabled because 'Skip All Comments' is enabled."
                            : "Post comments when review is complete."}
                        </Typography>
                      </div>
                      <Toggle
                        data-testid="integration-settings-completion-comments-toggle"
                        label={
                          <span className="sr-only">
                            Post Completion Comments
                          </span>
                        }
                        value="postCompletionComments"
                        checked={
                          skipAll
                            ? false
                            : commentBehavior.postCompletionComments !== false
                        }
                        disabled={skipAll}
                        onChange={(e) =>
                          updateCommentBehavior(
                            activeTab,
                            "postCompletionComments",
                            e.target.checked,
                          )
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Typography size="sm" weight="bold">
                          Skip Link Updates
                        </Typography>
                        <Typography size="xs" className="text-gray-500">
                          Skip posting review share links.
                        </Typography>
                      </div>
                      <Toggle
                        data-testid="integration-settings-skip-links-toggle"
                        label={
                          <span className="sr-only">Skip Link Updates</span>
                        }
                        value="skipLinkUpdates"
                        checked={commentBehavior.skipLinkUpdates || false}
                        onChange={(e) =>
                          updateCommentBehavior(
                            activeTab,
                            "skipLinkUpdates",
                            e.target.checked,
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Completion Rules (formerly Design Review Rules) — not yet implemented, wrike/asana only */}
            {activeTab !== "email" && (
              <Card>
                <div className="pointer-events-none grid gap-4 p-4 opacity-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <Typography as="h2" size="lg" weight="bold">
                        Completion Rules{" "}
                        <span className="text-gray-400 text-sm font-normal">
                          (Coming Soon)
                        </span>
                      </Typography>
                      <Typography size="sm" className="text-gray-500">
                        After a review completes, each rule is checked in order
                        against the task description. The first match determines
                        what happens.
                      </Typography>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => addDesignReviewRule(activeTab)}
                    >
                      Add Rule
                    </Button>
                  </div>

                  {designReviewRules.length === 0 ? (
                    <Typography size="sm" className="text-gray-400">
                      No completion rules configured.
                    </Typography>
                  ) : (
                    <div className="grid gap-3">
                      {designReviewRules.map((rule, index) => (
                        <div
                          key={index}
                          className="grid gap-3 rounded-lg border p-3"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="grid flex-1 gap-3">
                              <Input
                                label="Match text in task description"
                                placeholder="e.g., Corporate Design Review"
                                value={rule.descriptionContains}
                                invalid={!!errors[`rule-${index}`]}
                                helpText={
                                  errors[`rule-${index}`] ||
                                  "Case-sensitive substring match against the task description"
                                }
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                  updateDesignReviewRule(
                                    activeTab,
                                    index,
                                    "descriptionContains",
                                    e.target.value,
                                  )
                                }
                              />

                              <div className="flex items-center justify-between">
                                <div>
                                  <Typography size="sm" weight="bold">
                                    Tag stakeholders on completion
                                  </Typography>
                                  <Typography
                                    size="xs"
                                    className="text-gray-500"
                                  >
                                    @-mention configured tag users in the
                                    completion comment
                                  </Typography>
                                </div>
                                <Toggle
                                  label={
                                    <span className="sr-only">
                                      Tag stakeholders on completion
                                    </span>
                                  }
                                  value={`tagUsers-${index}`}
                                  checked={rule.tagUsersOnCompletion || false}
                                  onChange={(e) =>
                                    updateDesignReviewRule(
                                      activeTab,
                                      index,
                                      "tagUsersOnCompletion",
                                      e.target.checked,
                                    )
                                  }
                                />
                              </div>

                              <div className="flex items-center justify-between">
                                <div>
                                  <Typography size="sm" weight="bold">
                                    Skip task status update
                                  </Typography>
                                  <Typography
                                    size="xs"
                                    className="text-gray-500"
                                  >
                                    Don&apos;t change the task status when this
                                    rule matches
                                  </Typography>
                                </div>
                                <Toggle
                                  label={
                                    <span className="sr-only">
                                      Skip task status update
                                    </span>
                                  }
                                  value={`skipStatus-${index}`}
                                  checked={rule.skipStatusUpdate || false}
                                  onChange={(e) =>
                                    updateDesignReviewRule(
                                      activeTab,
                                      index,
                                      "skipStatusUpdate",
                                      e.target.checked,
                                    )
                                  }
                                />
                              </div>

                              <RuleBehaviorPreview rule={rule} />
                            </div>
                            <Button
                              variant="outline"
                              color="red"
                              onClick={() =>
                                removeDesignReviewRule(activeTab, index)
                              }
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="bg-gray-50 rounded p-2">
                    <Typography size="xs" className="text-gray-500">
                      If no rules match: status is updated but no comment is
                      posted.
                    </Typography>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>
      </Tabs.Root>

      <TestIntegrationDialog
        platform={activeTab}
        open={testDialogOpen}
        onOpenChange={setTestDialogOpen}
      />
    </article>
  );
}
